import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope, unwrapFanoutArray, firstVendor } from './_shared';

// ============================================
// Purge & Warm  (/cdn/domains/:d/{purge,warm})
// ============================================
export interface NodeAck {
  node_id: string;
  success: boolean;
  entries_purged: number;
  received_at: number;
}
export interface PurgeStatus {
  purge_id: string;
  total_nodes: number;
  acks: NodeAck[];
  created_at: number;
}
export function isPurgeComplete(s: PurgeStatus | null | undefined): boolean {
  return Boolean(s?.total_nodes && s.acks && s.acks.length >= s.total_nodes);
}

export function usePurge(
  domain: string,
  options?: UseMutationOptions<{ purge_id?: string } | null, unknown, { mode: string; values: string[] }>,
) {
  return useMutation({
    mutationFn: async ({ mode, values }: { mode: string; values: string[] }) => {
      const res = await serviceApiV3.post<Envelope<{ purge_id?: string }>>(`/cdn/domains/${domain}/purge`, { mode, values });
      return res.data?.data ?? null;
    },
    ...options,
  });
}

export function useWarm(
  domain: string,
  options?: UseMutationOptions<{ warm_id?: string; node_count?: number; url_count?: number } | null, unknown, string[]>,
) {
  return useMutation({
    mutationFn: async (urls: string[]) => {
      const res = await serviceApiV3.post<Envelope<unknown>>(`/cdn/domains/${domain}/warm`, { urls });
      // BE fans out: `{ data: { "<provider>": { warm_id, node_count, url_count } } }`.
      return firstVendor<{ warm_id?: string; node_count?: number; url_count?: number } | null>(res.data?.data, null);
    },
    ...options,
  });
}

/** Poll purge propagation every 3s; stop once all nodes ack. */
export function usePurgeStatus(domain: string, purgeId: string | null) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain, 'purge', purgeId ?? ''],
    enabled: !!purgeId,
    retry: false,
    refetchInterval: (query) => {
      const d = query.state.data as PurgeStatus | null | undefined;
      if (d && d.total_nodes > 0 && (d.acks?.length ?? 0) >= d.total_nodes) return false;
      return 3000;
    },
    queryFn: async (): Promise<PurgeStatus | null> => {
      if (!purgeId) return null;
      const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/purge/${purgeId}`);
      // BE fans out: `{ data: { "<provider>": { purge_id, total_nodes, acks } } }`.
      return firstVendor<PurgeStatus | null>(res.data?.data, null);
    },
  });
}

// ============================================
// Report Recipients  (/cdn/domains/:d/report-recipients)
// ============================================
export interface Recipient {
  id: number;
  email: string;
  frequency: string;
  enabled: boolean;
  created_at: string;
}

export function useReportRecipients(domain: string | undefined) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain ?? '', 'report-recipients'],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<Recipient[]> => {
      const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/report-recipients`);
      return unwrapFanoutArray<Recipient>(res.data?.data);
    },
  });
}

export function useAddReportRecipient(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, { email: string; frequency: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { email: string; frequency: string }) => {
      const res = await serviceApiV3.post(`/cdn/domains/${domain}/report-recipients`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'report-recipients'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteReportRecipient(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/report-recipients/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'report-recipients'] });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Bot Management analytics  (GET /cdn/domains/:d/bot-mgmt/analytics)
// ============================================
export function useBotMgmtAnalytics(domain: string | undefined, query: string) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain ?? '', 'bot-mgmt', 'analytics', query],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<Record<string, unknown> | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(
          `/cdn/domains/${domain}/bot-mgmt/analytics${query ? `?${query}` : ''}`,
        );
        // BE fans out: `{ data: { "<provider>": { overview, top_* , timeline } } }`.
        return firstVendor<Record<string, unknown> | null>(res.data?.data, null);
      } catch {
        return null;
      }
    },
  });
}
